import { IBusinessSolution } from '@aca-new/app/pages/main/shared/components/aside/shared/components/aside-menu/share/models/interfaces/business-solution.interface';
import { IServiceType } from '@aca-new/app/pages/main/shared/components/aside/shared/components/aside-menu/share/models/interfaces/service-type.interface';
import { IDynamicBooking, IDynamicBookingMenus } from '@aca-new/app/pages/main/shared/components/aside/shared/models/interfaces/dynamic-booking-menus.interface';
import { AsideService } from '@aca-new/app/pages/main/shared/components/aside/shared/services/aside.service';
import { IMAGES } from '@aca-new/app/shared/constants/images.constants';
import { buildProductInspectionsAsideServiceTypes } from '@aca-new/app/shared/constants/product-inspections-aside-service-types.constants';
import { EBusinessSolution } from '@aca-new/app/shared/models/enums/business-solution.enum';
import { EServiceType } from '@aca-new/app/shared/models/enums/service-type.enum';
import { QimaDividerService } from '@aca-new/app/shared/services/exported-services/qima-divider/qima-divider.service';
import { AcaMessageService } from '@aca-new/app/shared/services/post-message-services/aca-message/aca-message.service';
import { AppUserSettingService } from '@aca-new/app/shared/services/user-services/app-user-setting/app-user-setting.service';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EQimaButtonIconStyle, EQimaButtonSize, EQimaDividerOrientation, EQimaDividerStyle, EQimaIconName, QimaOptionalType } from '@qima/ngx-qima';
import { has } from 'lodash/index';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'app-aside-menu',
  templateUrl: './aside-menu.component.html',
  styleUrls: ['./aside-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AsideMenuComponent implements OnInit, OnChanges {
  /**
   * @description
   * Show aside menu or not
   * @type {boolean}
   * @default false
   */
  @Input('asideMenuIsVisible')
  public isVisible: boolean = false;

  /**
   * @description
   * Dynamic booking menus
   * @type {IDynamicBookingMenus}
   * @default {}
   */
  @Input('asideMenuDynamicBookingMenus')
  public dynamicBookingMenus: IDynamicBookingMenus = {};

  public isAcaFrameAvailable: boolean = this._acaMessageService.isAcaFrameAvailable;
  public isExpanded: boolean = false;
  public isAsideCollapsed: boolean = false;
  public serviceIcon: string = IMAGES.services;
  public closeButtonIconStyle: EQimaButtonIconStyle = EQimaButtonIconStyle.TERTIARY;
  public closeIconName: EQimaIconName = EQimaIconName.CLOSE;
  public closeButtonSize: EQimaButtonSize = EQimaButtonSize.SMALL;
  public dividerOrientation = EQimaDividerOrientation.HORIZONTAL;

  public businessSolutions: IBusinessSolution[] = [
    {
      id: EBusinessSolution.INSPECTIONS,
      label: 'APP_ASIDE.ASIDE_MENU.PRODUCT_INSPECTIONS',
      isVisible: !this._appUserSettingService.getIsWqsEnabled(),
      serviceTypes: buildProductInspectionsAsideServiceTypes(this._appUserSettingService),
    },
    {
      id: EBusinessSolution.LAB_TESTING,
      label: 'APP_ASIDE.ASIDE_MENU.TESTING',
      isVisible: !this._appUserSettingService.getIsWqsEnabled(),
      serviceTypes: [
        {
          id: EServiceType.LT,
          abbreviation: 'LT',
          label: 'APP_ASIDE.ASIDE_MENU.BOOK_LAB_TEST',
          path: this._appUserSettingService.getIsDisableLtInquiry() ? '/book-lab-test' : '/book-lt',
        },
      ],
    },
    {
      id: EBusinessSolution.CERTIFICATIONS,
      label: 'APP_ASIDE.ASIDE_MENU.CERTIFICATIONS_AND_VOC',
      isVisible: !this._appUserSettingService.getIsWqsEnabled(),
      serviceTypes: [
        {
          id: EServiceType.SABER,
          abbreviation: 'SABER',
          label: 'APP_ASIDE.ASIDE_MENU.SABER_CERTIFICATION',
          path: '/book-certification/saber',
        },
        {
          id: EServiceType.GMARK,
          abbreviation: 'GMARK',
          label: 'APP_ASIDE.ASIDE_MENU.G_MARK',
          path: '/book-certification/g-mark',
        },
        {
          id: EServiceType.SASOREGISTRATION,
          abbreviation: 'SASO',
          label: 'APP_ASIDE.ASIDE_MENU.SASO_REGISTRATION',
          path: '/book-certification/saso-registration',
        },
      ],
    },
    {
      id: EBusinessSolution.WQS,
      label: 'APP_ASIDE.ASIDE_MENU.WQS',
      isVisible: this.isFoodCertification,
      serviceTypes: [
        {
          id: EServiceType.GGAP,
          abbreviation: 'GGAP',
          label: 'APP_ASIDE.ASIDE_MENU.GGAP',
          path: this._appUserSettingService.getIsGgapOrderEnabled() ? '/book-food-certification/ggap/null' : '/book-wqs/ggap',
          isVisible: this._appUserSettingService.getIsWqsEnabled() && this.isAcaFrameAvailable,
        },
        {
          id: EServiceType.BRCGS,
          abbreviation: 'BRCGS',
          label: 'APP_ASIDE.ASIDE_MENU.BRCGS',
          path: '/book-food-certification/brcgs/null',
          isVisible: this._appUserSettingService.getIsBRCGSEnabled(),
        },
        {
          id: EServiceType.IFS,
          abbreviation: 'IFS',
          label: 'APP_ASIDE.ASIDE_MENU.IFS',
          path: '/book-food-certification/ifs/null',
          isVisible: this._appUserSettingService.getIsIfsEnabled(),
        },
        {
          id: EServiceType.SQF,
          abbreviation: 'SQF',
          label: 'APP_ASIDE.ASIDE_MENU.SQF',
          path: '/book-food-certification/sqf/null',
          isVisible: this._appUserSettingService.getIsSqfEnabled(),
        },
      ],
    },
    {
      id: EBusinessSolution.AUDITS,
      label: 'APP_ASIDE.ASIDE_MENU.SUPPLIER_AUDITS_TRAINING_WORKER_VOICE',
      serviceTypes: [
        {
          id: EServiceType.EA,
          abbreviation: 'EA',
          label: 'APP_ASIDE.ASIDE_MENU.ETHICAL_AUDIT',
          path: '/book-audit/ea',
        },
        {
          id: EServiceType.MA,
          abbreviation: 'MA',
          label: 'APP_ASIDE.ASIDE_MENU.MANUFACTURING',
          path: '/book-audit/ma',
          isVisible: !this._appUserSettingService.getIsWqsEnabled(),
        },
        {
          id: EServiceType.DR,
          abbreviation: 'DR',
          label: 'APP_ASIDE.ASIDE_MENU.DESKTOP_REVIEW',
          isVisible: !this._appUserSettingService.getIsWqsEnabled(),
          path: '/book-audit/dr',
        },
        {
          id: EServiceType.CTPAT,
          abbreviation: 'CTPAT',
          label: 'APP_ASIDE.ASIDE_MENU.C_TPAT',
          path: '/book-audit/ctpat',
          isVisible: !this._appUserSettingService.getIsQimaWQS() || !this._appUserSettingService.getIsWqsEnabled(),
        },
        {
          id: EServiceType.STRA,
          abbreviation: 'STRA',
          label: 'APP_ASIDE.ASIDE_MENU.STRUCTURAL_AUDIT',
          path: '/book-audit/stra',
          isVisible: !this._appUserSettingService.getIsQimaWQS() || !this._appUserSettingService.getIsWqsEnabled(),
        },
        {
          id: EServiceType.ENVA,
          abbreviation: 'ENVA',
          label: 'APP_ASIDE.ASIDE_MENU.ENVIRONMENTAL_AUDIT',
          path: '/book-audit/enva',
          isVisible: !this._appUserSettingService.getIsWqsEnabled(),
        },
        {
          id: EServiceType.GMP,
          abbreviation: 'GMP',
          label: 'APP_ASIDE.ASIDE_MENU.GOOD_MANUFACTURING',
          path: '/book-audit/gmp',
          isVisible: !this._appUserSettingService.getAuditBookingMenusInvisibility()?.GMP && !this._appUserSettingService.getIsWqsEnabled(),
        },
        {
          id: EServiceType.GHP,
          abbreviation: 'GHP',
          label: 'APP_ASIDE.ASIDE_MENU.GOOD_HYGIENE',
          path: '/book-audit/ghp',
          isVisible: !this._appUserSettingService.getAuditBookingMenusInvisibility()?.GHP && !this._appUserSettingService.getIsWqsEnabled(),
        },
      ],
    },
  ];

  public serviceTypesActive: IServiceType[] = [];
  public asideMenuBusinessSolutionIndex: QimaOptionalType<number> = undefined;
  public asideMenuServiceTypeIndex: QimaOptionalType<number> = undefined;
  public dividerStyle: EQimaDividerStyle = EQimaDividerStyle.PEARL;

  public constructor(
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _appUserSettingService: AppUserSettingService,
    private readonly _asideService: AsideService,
    private readonly _router: Router,
    private readonly _acaMessageService: AcaMessageService,
    public readonly qimaDivider: QimaDividerService
  ) {}

  public get isFoodCertification(): boolean {
    return (this._appUserSettingService.getIsWqsEnabled() || this._appUserSettingService.getIsBRCGSEnabled()) && this.isAcaFrameAvailable;
  }

  public get sectionIcon(): string {
    return this.isFoodCertification ? IMAGES.synchronizeArrows : IMAGES.upcomingIcon;
  }

  public get sectionTitle(): string {
    return this.isFoodCertification ? 'APP_BOOK_SUB_MENU.TITLE_RENEW' : 'APP_BOOK_SUB_MENU.TITLE';
  }

  public ngOnInit(): void {
    this._watch();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.isAcaFrameAvailable) {
      this._onDynamicBookingMenusChange(changes);
    }
  }

  public onCloseClick(): void {
    this._closeAsideMenu();
  }

  public onAsideMenuBusinessSolutionClick(index: Readonly<number>): void {
    this.asideMenuBusinessSolutionIndex = index;
    this.serviceTypesActive = this.businessSolutions[index].serviceTypes.filter((item: IServiceType): boolean => item.isVisible !== false);
    this.isExpanded = true;
  }

  public onAsideMenuServiceTypeClick(index: Readonly<number>, item: Readonly<IServiceType>): void {
    this.asideMenuServiceTypeIndex = index;
    this._asideService.updateAsideMenuVisible(false);

    if (this.isAcaFrameAvailable) {
      this._acaMessageService.bookProgressHandler(item.path);
    } else {
      void this._router.navigateByUrl(item.path);
    }
  }

  private _watch(): void {
    this._asideService.isAsideCollapsed$.pipe(untilDestroyed(this)).subscribe((isAsideCollapsed: boolean): void => {
      this.isAsideCollapsed = isAsideCollapsed;
      this._changeDetectorRef.markForCheck();
    });
  }

  private _closeAsideMenu(): void {
    this._asideService.updateAsideMenuVisible(false);
  }

  private _onDynamicBookingMenusChange(changes: SimpleChanges): void {
    if (has(changes, 'dynamicBookingMenus') && changes.dynamicBookingMenus.currentValue !== changes.dynamicBookingMenus.previousValue) {
      if (this.isAcaFrameAvailable) {
        this._updateBusinessSolutions();
      }
    }
  }

  private _generateServiceType(menu: IDynamicBooking, key: string): IServiceType {
    return {
      id: menu.serviceSimpleName.toLocaleLowerCase() as EServiceType,
      label: `${menu.serviceName}`,
      path: `/booking/${key.toLocaleLowerCase()}/${menu.serviceSimpleName.toLocaleLowerCase()}`,
      abbreviation: menu.serviceSimpleName === 'CSM' ? 'WCP' : menu.serviceSimpleName,
    };
  }

  private _updateBusinessSolutions(): void {
    const keys = Object.keys(this.dynamicBookingMenus);
    const serviceSaqType: IServiceType[] = [];
    const serviceOtherTypes: IServiceType[] = [];

    this.businessSolutions = this.businessSolutions.filter((item: IBusinessSolution): boolean => !keys.includes(item.label) && item.isVisible !== false);

    keys.forEach((key: string): void => {
      const isSaq: boolean = key.toLowerCase() === EBusinessSolution.SELF_ASSESSMENT;

      this.dynamicBookingMenus[key].forEach((menu: IDynamicBooking): void => {
        const serviceType = this._generateServiceType(menu, key);

        if (isSaq) {
          serviceSaqType.push(serviceType);
        } else {
          serviceOtherTypes.push(serviceType);
        }
      });

      if (isSaq && !this._appUserSettingService.getEnableFoodCertification() && this.isAcaFrameAvailable) {
        this.businessSolutions.push({
          id: key as EBusinessSolution,
          label: 'COMMON.TRACEABILITY',
          serviceTypes: serviceSaqType,
        });
      }
    });

    if (serviceOtherTypes.length) {
      this.businessSolutions.forEach((item): void => {
        if (item.id === EBusinessSolution.AUDITS) {
          item.serviceTypes = [...item.serviceTypes, ...serviceOtherTypes];
        }
      });
    }

    this._changeDetectorRef.markForCheck();
  }
}
